import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetOralExamCohortStateQueryKey,
  getGetOralExamIdPhotoUrlQueryKey,
  useGetOralExamCohortState,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { useOralExamLabOverrides } from '@/common/oralExam/oralExamLabOverrides';
import { mergeCohortCachePreservingIdReview } from './applyIdReviewToCohortCache';
import { ORAL_EXAM_SAFETY_POLL_MS } from './useOralExamRunStatePoll';
import { unwrapOralExamCohortState } from './unwrapOralExamCohortState';

/**
 * Poll cohort aggregate state for the lecturer supervision console.
 */
export function useOralExamCohortState(
  slotId: string,
  options?: { enabled?: boolean; polling?: boolean },
) {
  const lab = useOralExamLabOverrides();
  const enabled = options?.enabled !== false && Boolean(slotId) && !lab?.cohortState;

  const query = useGetOralExamCohortState(slotId, {
    query: {
      enabled,
      refetchInterval: options?.polling ? ORAL_EXAM_SAFETY_POLL_MS : false,
      placeholderData: (previousData) => previousData,
      structuralSharing: (previousData, newData) => (
        mergeCohortCachePreservingIdReview(previousData, newData) as typeof newData
      ),
    },
  });

  const cohort = useMemo(
    () => unwrapOralExamCohortState(query.data),
    [query.data],
  );

  const bookingRuns = useMemo(
    (): OralExamBookingRunSummary[] => cohort?.bookingRuns ?? [],
    [cohort],
  );

  // Slot capacity is the authoritative 1:1-vs-cohort signal (matches the backend:
  // capacity >= 2 => 1:N cohort with the AI examiner + mandatory group briefing).
  // Route on this, NOT on bookingRuns.length — an under-subscribed cohort slot
  // (capacity 2, one booking) is still a cohort exam. Falls back to the booked
  // count only while the cohort state is still loading (cohort == null).
  const capacity = cohort?.capacity ?? bookingRuns.length;

  const queryKey = useMemo(
    () => getGetOralExamCohortStateQueryKey(slotId),
    [slotId],
  );

  const queryClient = useQueryClient();
  const prevIdPhotoRef = useRef<Map<string, { document: string | null; selfie: string | null }>>(new Map());

  // The photo URL query is keyed by (slot, booking, kind) only — it has no idea a
  // retake happened server-side unless told to refetch. Watching the STATUS
  // transition alone (e.g. rejected -> submitted) missed real cases: a mid-flow
  // selfie-only retake that never leaves 'submitted', or a document-only retake
  // after rejection while the selfie blob is unchanged. Compare the actual blob
  // path values per photo kind instead — any change means the underlying blob
  // moved and the cached SAS URL for that kind must be invalidated, regardless of
  // what `idVerification.status` happens to read at that instant.
  useEffect(() => {
    if (!cohort?.bookingRuns) return;

    for (const run of cohort.bookingRuns) {
      const { bookingId } = run;
      const current = {
        document: run.idPhotoBlobPath ?? null,
        selfie: run.idSelfiePhotoBlobPath ?? null,
      };
      const prev = prevIdPhotoRef.current.get(bookingId);

      if (prev && prev.document !== current.document) {
        queryClient.invalidateQueries({
          queryKey: getGetOralExamIdPhotoUrlQueryKey(slotId, bookingId, { kind: 'document' }),
        });
      }
      if (prev && prev.selfie !== current.selfie) {
        queryClient.invalidateQueries({
          queryKey: getGetOralExamIdPhotoUrlQueryKey(slotId, bookingId, { kind: 'selfie' }),
        });
      }

      prevIdPhotoRef.current.set(bookingId, current);
    }
  }, [cohort, queryClient, slotId]);

  if (lab?.cohortState) {
    return { ...lab.cohortState, error: null };
  }

  return {
    cohort,
    bookingRuns,
    capacity,
    isLoading: query.isLoading,
    error: query.error,
    etag: cohort?.etag ?? null,
    queryKey,
  };
}
